import React, { useEffect, useRef, useState } from "react";
import {
    collection,
    addDoc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
    arrayUnion,
    arrayRemove,
    setDoc,
    runTransaction
} from "firebase/firestore";
import { db } from "../../Utils/firebase";
import { FIREBASE_CONVERSATIONS } from "../../Utils/constant";
import { getProfile } from "../../actions/login";
import SendChat from "../Chat/SendChat";
import ReceiveChat from "../Chat/ReceiveChat";
import { MessageBox } from "../Chat/ChatInput";
import { formatTime } from "../../Utils/helpers";

const JournalSharedChatSection = ({ journal, originalSender, messageId, conversationId, originalMessage }) => {
    const [userProfile, setUserProfile] = useState(null);
    const [messages, setMessages] = useState([]);
    const messagesEndRef = useRef(null);

    // Calculate threadId from conversationId and messageId
    const threadId = (conversationId && messageId) ? `${conversationId}_${messageId}` : null;

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const profile = await getProfile();
                setUserProfile(profile);
            } catch (error) {
                console.error("Error fetching user profile:", error);
            }
        };
        fetchUserProfile();
    }, []);



    // Initialize thread on component mount
    useEffect(() => {
        const initializeThread = async () => {
            if (!threadId || !conversationId || !messageId) return;

            try {
                const threadRef = doc(db, "threads", threadId);
                const threadSnap = await getDoc(threadRef);

                if (!threadSnap.exists()) {
                    // Get conversation data for participants
                    const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
                    const convoSnap = await getDoc(convoRef);

                    if (convoSnap.exists()) {
                        const convoData = convoSnap.data();
                        const visibleToUserIds = convoData.participants || [];

                        // Get the original message data from Firestore
                        const originalMessageRef = doc(db, FIREBASE_CONVERSATIONS, conversationId, "messages", messageId);
                        const originalMsgSnap = await getDoc(originalMessageRef);

                        if (originalMsgSnap.exists()) {
                            const originalMsgData = originalMsgSnap.data();

                            // If original message is missing journal.title_answer, try to supplement it
                            // from the `originalMessage` prop (passed by the parent components like SendChat/ReceiveChat)
                            if (originalMsgData && originalMsgData.journal && !originalMsgData.journal.title_answer) {
                                const fallbackTitle = originalMessage?.journal?.title_answer || originalMessage?.title_answer || null;
                                if (fallbackTitle) {
                                    originalMsgData.journal = {
                                        ...originalMsgData.journal,
                                        title_answer: fallbackTitle,
                                    };
                                }
                            }

                            // Create thread document with complete original message data
                            await setDoc(threadRef, {
                                createdAt: serverTimestamp(),
                                id: threadId,
                                visibleTo: visibleToUserIds,
                                mainMessage: originalMsgData,
                                conversationId: conversationId
                            });

                            // Add the original message as the first message in the thread (same as Chat.jsx)
                            await addDoc(collection(db, "threads", threadId, "messages"), originalMsgData);


                        }
                    }
                }
            } catch (error) {
                console.error("Error initializing thread:", error);
            }
        };

        initializeThread();
    }, [threadId, conversationId, messageId]);

    // Listen to messages in thread
    useEffect(() => {
        if (!threadId) return;

        const messagesRef = collection(db, "threads", threadId, "messages");
        const q = query(messagesRef, orderBy("timestamp", "asc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(prevMessages => {
                const threadMessages = [];
                snapshot.forEach((doc) => {
                    const newMessageData = { id: doc.id, ...doc.data() };
                    const existingMessage = prevMessages.find(msg => msg.id === doc.id);
                    if (existingMessage && existingMessage.isEditing) {
                        newMessageData.isEditing = true;
                    }
                    threadMessages.push(newMessageData);
                });
                return threadMessages;
            });
        }, (error) => {
            console.error("Error listening to messages:", error);
        });

        return () => unsubscribe();
    }, [threadId]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);



    const handleSendJournalThreadMessage = async (content, file = null) => {
        if (!userProfile || !threadId || !conversationId || !messageId || !content.trim()) return;

        try {
            // Get conversation data for participants
            const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
            const convoSnap = await getDoc(convoRef);

            if (!convoSnap.exists()) {
                console.error("Conversation not found");
                return;
            }

            const convoData = convoSnap.data();
            const visibleToUserIds = convoData.participants || [];

            const messageData = {
                type: "text",
                metadata: {},
                reactions: {},
                isRead: null,
                senderId: userProfile.id,
                senderName: userProfile.full_name,
                senderProfileImage: userProfile.image,
                timestamp: serverTimestamp(),
                content: content,
                isFile: !!file,
                visibleTo: visibleToUserIds
            };

            if (file) {
                messageData.file = {
                    thumbnail: file.thumbnail || null,
                    url: file.video_url || file.media_url || file.audio_url || null,
                    type: file.type || null,
                };
            }

            // Update original message's replyTo field using transaction
            const originalMessageRef = doc(db, FIREBASE_CONVERSATIONS, conversationId, "messages", messageId);

            await runTransaction(db, async (transaction) => {
                const originalMsgSnap = await transaction.get(originalMessageRef);

                if (originalMsgSnap.exists()) {
                    const currentReplyTo = originalMsgSnap.data()?.replyTo || { count: 0 };

                    const replyToUpdate = {
                        id: threadId,
                        count: (currentReplyTo.count || 0) + 1,
                        message: content,
                    };

                    // Include file info if replying with a file
                    if (file) {
                        replyToUpdate.file = {
                            thumbnail: file.thumbnail || null,
                            url: file.video_url || file.media_url || file.audio_url || null,
                            type: file.type || null,
                        };
                    }

                    transaction.update(originalMessageRef, {
                        replyTo: replyToUpdate
                    });
                }
            });

            // Add message to thread subcollection
            await addDoc(collection(db, "threads", threadId, "messages"), messageData);

        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const handleLike = async (messageId, emoji) => {
        if (!userProfile || !threadId) return;
        try {
            const messageRef = doc(db, "threads", threadId, "messages", messageId);
            const messageSnap = await getDoc(messageRef);
            if (messageSnap.exists()) {
                const messageData = messageSnap.data();
                const currentReactions = messageData.reactions || {};
                const currentEmojiUsers = currentReactions[emoji] || [];
                const userId = String(userProfile.id);

                let updatedReactions = { ...currentReactions };

                if (currentEmojiUsers.includes(userId)) {
                    // Remove user from this emoji
                    updatedReactions[emoji] = arrayRemove(userId);
                } else {
                    // Add user to this emoji
                    updatedReactions[emoji] = arrayUnion(userId);
                }

                await updateDoc(messageRef, { [`reactions.${emoji}`]: updatedReactions[emoji] });
            }
        } catch (error) {
            console.error("Error liking message:", error);
        }
    };

    const handleEdit = (messageId, _oldText) => {
        setMessages(prev => prev.map(msg =>
            msg.id === messageId ? { ...msg, isEditing: true } : { ...msg, isEditing: false }
        ));
    };

    const handleSaveEdit = async (messageId, newContent) => {
        if (!threadId || !newContent.trim()) return;
        try {
            const messageRef = doc(db, "threads", threadId, "messages", messageId);
            await updateDoc(messageRef, { content: newContent, editedAt: serverTimestamp() });
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? { ...msg, isEditing: false, content: newContent } : msg
            ));
        } catch (error) {
            console.error("Error editing message:", error);
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? { ...msg, isEditing: false } : msg
            ));
        }
    };

    const handleCancelEdit = (messageId) => {
        setMessages(prev => prev.map(msg =>
            msg.id === messageId ? { ...msg, isEditing: false } : msg
        ));
    };

    const handleDeleteMessage = async (messageId) => {
        if (!threadId || !userProfile) return;
        try {
            const messageRef = doc(db, "threads", threadId, "messages", messageId);
            await deleteDoc(messageRef);
        } catch (error) {
            console.error("Error deleting message:", error);
        }
    };

    return (
        <div
            className="chat-section"
            style={{
                flex: 1,
                backgroundColor: "#121E29",
                borderRadius: "24px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}
        >
            {/* Chat Header */}
            <div
                className="chat-header"
                style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px 20px",
                    borderBottom: "1px solid #333",
                }}
            >
                <img
                    src={originalSender?.image || "/icons/james.svg"}
                    alt="Profile"
                    style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
                />
                <div style={{ marginLeft: "12px" }}>
                    <div style={{ color: "#e6ecf1", fontWeight: "500", fontSize: "16px" }}>
                        {originalSender?.name || "Journal Chat"}
                    </div>
                    <div style={{ color: "#99D1FF", fontSize: "12px" }}>
                        {journal?.emotion_name} Journal Discussion
                    </div>
                </div>
            </div>

            {/* Messages Container */}
            <div
                className="messages-container"
                style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}
            >
                {messages.length === 0 ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#666", fontSize: "14px" }}>
                        Start a conversation about this journal...
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        // Skip the first message (original journal message) since it's just for context
                        // The actual journal is already visible above the modal
                        if (index === 0 && msg.journal?.isJournalShared) {
                            return null;
                        }

                        if (msg.senderId === userProfile?.id) {
                            return (
                                <ReceiveChat
                                    key={msg.id}
                                    response={msg.content}
                                    responder="You"
                                    time={msg.timestamp ? formatTime(msg.timestamp) : "Now"}
                                    userImage={userProfile?.image || "/icons/james.svg"}
                                    userId={msg.senderId}
                                    isFile={msg.isFile}
                                    fileInfo={msg?.file}
                                    fileType={msg?.file?.type}
                                    messageId={msg.id}
                                    conversationId={threadId}
                                    replyCount={0}
                                    isEditing={!!msg.isEditing}
                                    isLiked={Array.isArray(msg.reactions?.like) ? msg.reactions.like.includes(String(userProfile?.id)) : false}
                                    reactionSummary={msg.reactions || {}}
                                    showReply={false}
                                    onLike={handleLike}
                                    onEdit={handleEdit}
                                    onSaveEdit={handleSaveEdit}
                                    onCancelEdit={handleCancelEdit}
                                    onDelete={handleDeleteMessage}
                                    journal={{}}
                                />
                            );
                        } else {
                            return (
                                <SendChat
                                    key={msg.id}
                                    username={msg.senderName}
                                    title={msg.content}
                                    time={msg.timestamp ? formatTime(msg.timestamp) : "Now"}
                                    userImage={msg.senderProfileImage || "/icons/james.svg"}
                                    senderId={msg.senderId}
                                    isFile={msg.isFile}
                                    fileInfo={msg?.file}
                                    fileType={msg?.file?.type}
                                    messageId={msg.id}
                                    conversationId={threadId}
                                    replyCount={0}
                                    isLiked={Array.isArray(msg.reactions?.like) ? msg.reactions.like.includes(String(userProfile?.id)) : false}
                                    reactionSummary={msg.reactions || {}}
                                    showReply={false}
                                    onLike={handleLike}
                                    journal={{}}
                                />
                            );
                        }
                    })
                )}
                <div ref={messagesEndRef} />

                {messages.some((msg) => msg.isEditing) ? (
                    <MessageBox
                        onSendClick={(newContent) =>
                            handleSaveEdit(
                                messages.find((msg) => msg.isEditing)?.id,
                                newContent
                            )
                        }
                        initialText={messages.find((msg) => msg.isEditing)?.content || ""}
                        isEditing={true}
                        onCancelEdit={() =>
                            handleCancelEdit(messages.find((msg) => msg.isEditing)?.id)
                        }
                        isExpandedByDefault={false}
                    />
                ) : (
                    <MessageBox onSendClick={handleSendJournalThreadMessage} isExpandedByDefault={false} />
                )}
            </div>
        </div>
    );
};

export default JournalSharedChatSection;
